// usePostcodeSync - Hook to detect and handle postcode changes across devices
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMe } from '../auth/hooks/useMe'
import { useToast } from '../components/radix'
import { normalizePostcode } from '../services/postcodes'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

/**
 * Detects when postcode has changed on another device and redirects user to sync.
 *
 * Compares sessionStorage postcode with User.last_postcode from database.
 * If they don't match, it means user changed postcode on another device.
 *
 * When mismatch detected:
 * 1. Clears sessionStorage
 * 2. Shows notification
 * 3. Redirects to JobSetup
 * 4. JobSetup will fetch coordinates from postcodes.io and restore session
 *
 * @param session - Current engineer session from sessionStorage
 */
export function usePostcodeSync(session: EngineerSession | null) {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const { data: meData } = useMe()
  const { addToast } = useToast()
  const [hasCheckedSync, setHasCheckedSync] = useState(false)

  useEffect(() => {
    if (!session || !meData || hasCheckedSync) return

    // Check if we just set a new postcode (skip sync check for 10 seconds)
    const justSetPostcode = sessionStorage.getItem('postcodeJustSet')
    if (justSetPostcode) {
      const timestamp = parseInt(justSetPostcode, 10)
      const elapsed = Date.now() - timestamp

      // Skip check for 10 seconds after setting postcode (increased from 5)
      if (elapsed < 10000) {
        console.log('[PostcodeSync] Skipping check - postcode was just set')
        setHasCheckedSync(true)

        // DON'T remove flag - keep it for full 10 seconds so all pages skip check
        // Flag will be removed after 10 seconds or when actual mismatch is detected
        return
      }

      // Flag is stale (>10 seconds old), remove it now
      console.log('[PostcodeSync] Grace period expired, removing flag')
      sessionStorage.removeItem('postcodeJustSet')
    }

    // Compare sessionStorage postcode with database postcode, ignoring
    // formatting: only a different *place* is a cross-device change. Trimming
    // alone left the internal space significant, so a profile still holding a
    // pre-OCTO-550159 "DE118GL" looked like a move away from the "DE11 8GL"
    // the restore had just written — clearing the session and bouncing the
    // user back here on a loop.
    const sessionPostcode = normalizePostcode(session.postcode)
    const dbPostcode = meData.last_postcode
      ? normalizePostcode(meData.last_postcode)
      : undefined

    if (dbPostcode && sessionPostcode !== dbPostcode) {
      // Postcode changed on another device!
      console.log(
        `[PostcodeSync] Mismatch detected: session="${sessionPostcode}", db="${dbPostcode}"`
      )

      // Clear local sessionStorage (including the flag)
      sessionStorage.removeItem('engineerSession')
      sessionStorage.removeItem('postcodeJustSet')

      // Show notification. Uses the stored spelling, not the compacted form
      // the comparison runs on, so the toast reads "DE11 8GL" not "DE118GL".
      addToast(
        `Location changed to ${meData.last_postcode} on another device. Redirecting...`,
        'info',
        3000
      )

      // Redirect to JobSetup to restore from database
      // JobSetup will fetch coordinates from postcodes.io API
      setTimeout(() => {
        navigate(`/${tenantSlug}/engineer`)
      }, 1500)
    }

    setHasCheckedSync(true)
  }, [session, meData, hasCheckedSync, navigate, tenantSlug, addToast])
}
